import {
  Grid,
  useTheme
} from "@mui/material";
import {
  Fragment,
  useEffect,
  useRef,
  useState
} from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useParams
} from "react-router-dom";
import { Spinner } from "../../components/Spinner";
import { LEVEL } from "../../consts";
import { useStartOrResumeQuery } from "../../queries";
import { useAuthStore } from "../../stores";
import type {
  LearningActivity,
  Level
} from "../../types";
import { getEnvironmentVariables } from "../../utils/general";
import { getScormTrackerInitText } from "../../utils/scormTracker";

const { scormJs } = getEnvironmentVariables();
export function Scorm() {

  const theme = useTheme();
  const { t } = useTranslation();
  const {
    id: idString,
    learningObjectTypology: activityLevel
  } = useParams<{ id: string, learningObjectTypology: Level}>();
  const activityId = idString ? parseInt(idString) : NaN;
  const location = useLocation();
  const calculatedCourseId = location.search.split("courseId=")[1]?.split("&")[0];
  const calculatedRootId = location.search.split("rootId=")[1]?.split("&")[0];
  const language = location.search.split("lang=")[1]?.split("&")[0];
  const hasPostMessageData = location.search.split("hasPostMessageData=")[1]?.split("&")[0] === "true";


  // const { search } = useLocation();
  // const query = new URLSearchParams(search);

  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken);
  const userData = useAuthStore(state => state.userData);
  const flagScript = useRef(false);
  const [dataFromPostMessage, setDataFromPostMessage] = (
    useState<LearningActivity | null>(null)
  );
  const [isWaitingForData, setIsWaitingForData] = useState(hasPostMessageData);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "START_OR_RESUME_DATA") {
        setDataFromPostMessage(event.data.data);
        setIsWaitingForData(false);
      }
    };
    window.addEventListener("message", handleMessage);

    if (hasPostMessageData) {
      window.parent.postMessage({ type: "SCORM_READY" }, window.location.origin);
    }

    // Safety fallback: if message doesn't arrive in 5 seconds, stop waiting
    let timeout: ReturnType<typeof setTimeout>;
    if (hasPostMessageData) {
      timeout = setTimeout(() => {
        setIsWaitingForData(false);
      }, 5000);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
      if (timeout) clearTimeout(timeout);
    };
  }, [hasPostMessageData]);

  // const courseId = query.get("courseId");
  // const rootId = query.get("rootId");

  let parentId = "";
  let grandParentId = "";
  if ((calculatedRootId && calculatedRootId !== "null") && (calculatedCourseId && calculatedCourseId !== "null")) {
    parentId = calculatedCourseId?.toString();
    grandParentId = calculatedRootId?.toString();
  } else {
    if ((calculatedRootId && calculatedRootId !== "null")) {
      parentId = calculatedRootId?.toString();
    } else if (calculatedCourseId && calculatedCourseId !== "null") {
      parentId = calculatedCourseId?.toString();
    } else {
      parentId = "";
    }
  }

  const {
    data: learningObjectQueryData,
    isLoading: startOrResumeIsLoading,
    fetchStatus: fetchStatusQuery
  } = useStartOrResumeQuery({
    enabled: !dataFromPostMessage && !isWaitingForData,
    grandParentId: grandParentId,
    language,
    learningObjectId: activityId,
    learningObjectTypology: activityLevel as Level,
    parentId: parentId
  });

  const learningObject = dataFromPostMessage || learningObjectQueryData;
  const fetchStatus = dataFromPostMessage ? "idle" : fetchStatusQuery;
  const isLoading = dataFromPostMessage ? false : startOrResumeIsLoading;

  const idPath = calculatedRootId && (calculatedRootId !== "null")
    ? calculatedRootId?.toString()
    : calculatedCourseId && (calculatedCourseId !== "null")
      ? calculatedCourseId?.toString()
      : activityId.toString();
  const typologyPath = calculatedRootId !== "null"
    ? LEVEL.PATH
    : calculatedCourseId !== "null"
      ? LEVEL.COURSE
      : activityLevel;
  const detailsPath = `esplora/dettaglio/${idPath}/${typologyPath}`;

  useEffect(() => {
    if (
      accessToken
      && fetchStatus === "idle"
      && learningObject
      && scormJs
      && sessionToken
      && !isLoading
      && userData
    ) {
      if (!flagScript.current) {
        const libraryScript = (callback: () => void) => {
          const libraryScript = document.createElement("script");
          libraryScript.src = `${scormJs}?hash=${sessionToken}`;
          libraryScript.type = "text/javascript";
          libraryScript.setAttribute("data-react-helmet", "true");
          libraryScript.onload = () => callback();
          document.body.appendChild(libraryScript);
        };
        const libraryInit = () => {
          const libraryInit = document.createElement("script");
          libraryInit.type = "text/javascript";
          libraryInit.text = getScormTrackerInitText({
            accessToken,
            calculatedCourseId,
            calculatedRootId,
            detailsPath,
            language,
            learningObject,
            sessionToken,
            userData
          });
          document.body.appendChild(libraryInit);
        };
        libraryScript(libraryInit);
        flagScript.current = true;
      }
    }

  }, [
    accessToken, detailsPath, fetchStatus, learningObject,
    sessionToken, isLoading, userData, calculatedCourseId, calculatedRootId, language
  ]);
  const isToj = activityLevel === LEVEL.DA_FINAL_BALANCE ||
  activityLevel === LEVEL.DA_GOALS ||
  activityLevel === LEVEL.DA_MATERIAL ||
  activityLevel === LEVEL.DA_MEETING;

  return (
    <Fragment>
      <Helmet>
        <meta charSet="utf-8"/>
        <title>{ t("app_name") }</title>
        <style type="text/css">
          { `body { background: ${ !isToj ?
            theme.palette.background?.paper :
            theme.linearGradients.gradientB}  ` }
        </style>
      </Helmet>
      <Grid
        display={ "flex" }
        alignItems={ "center" }
        justifyContent={ "center" }
        width={ "100%" }
        height={ "calc(100% - 72px)" }
        position={ "absolute" }
        bottom={ 0 }
        left={ 0 }
      >
        <Spinner
          alignSelf="center"
          padding={ 4 }
          size={ 80 }
          thickness={ 3 }
        />
      </Grid>
    </Fragment>
  );
}
